import React, { useState } from 'react';
import { User } from '../types';
import { AVATAR_OPTIONS, getAvatarCategories, type AvatarData } from '../utils/avatarUtils';

interface AvatarSelectorProps {
  currentUser: User | null;
  onAvatarSelect: (avatarId: string) => void;
  onClose: () => void;
}

const AvatarSelector: React.FC<AvatarSelectorProps> = ({
  currentUser,
  onAvatarSelect,
  onClose
}) => {
  const [selectedCategory, setSelectedCategory] = useState<'animals' | 'faces' | 'objects' | 'nature' | 'symbols'>('animals');
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);

  const categories = getAvatarCategories();

  const filteredAvatars = AVATAR_OPTIONS.filter(avatar => avatar.category === selectedCategory);

  const handleAvatarClick = (avatarId: string) => {
    setSelectedAvatar(avatarId);
  };

  const handleConfirm = () => {
    if (selectedAvatar) {
      onAvatarSelect(selectedAvatar);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Choose Your Avatar</h2>
              <p className="text-indigo-100 mt-1">
                Select from our safe, pre-approved avatar collection
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-indigo-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Category Selection */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Categories</h3>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="mr-2">{category.icon}</span>
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Avatar Grid */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {categories.find(c => c.id === selectedCategory)?.name} Avatars
            </h3>
            <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-3 max-h-64 overflow-y-auto">
              {filteredAvatars.map((avatar) => (
                <button
                  key={avatar.id}
                  onClick={() => handleAvatarClick(avatar.id)}
                  className={`p-3 rounded-lg border-2 transition-all hover:scale-105 ${
                    selectedAvatar === avatar.id
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  title={avatar.name}
                >
                  <div 
                    className="text-3xl mb-1"
                    style={{ color: avatar.color }}
                  >
                    {avatar.emoji}
                  </div>
                  <div className="text-xs text-gray-600 truncate">
                    {avatar.name}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* COPPA Compliance Notice */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-green-800">Safe & Compliant</h4>
                <p className="text-sm text-green-700 mt-1">
                  All avatars are pre-approved and safe for children. No personal photos or uploads are required.
                  This ensures full COPPA compliance and protects your privacy.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedAvatar}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              Select Avatar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvatarSelector;
